import { styled } from "@mui/material/styles";
import {
  Swiper,
  SwiperProps
} from "swiper/react";
import type { CarouselProps } from "./Carousel.types";

import { StyledPortalWrapper } from "../../components/CarouselCardRestyle/Card.styles";
import {
  CAROUSEL_TYPE
} from "../../consts";

type StyledSwiperProps =
  SwiperProps &
  Pick<
    CarouselProps,
    "carouselType"
  > & {
    $isMobile?: boolean
    $label?: string
  }

export const StyledSwiper = styled(Swiper)<
  Omit<StyledSwiperProps, "carouselType"> & {
    $carouselType: StyledSwiperProps["carouselType"],
  }
>(({
  theme,
  $carouselType,
  $label,
  $isMobile
}) => ({
  alignItems: ($carouselType === CAROUSEL_TYPE.ZOOM) ? "center": undefined,
  display: ($carouselType === CAROUSEL_TYPE.ZOOM) && !$isMobile ? "flex": undefined,
  minHeight:
    ($carouselType === CAROUSEL_TYPE.HERO)
      ? $isMobile ? "418px" : "450px"
      : $carouselType === CAROUSEL_TYPE.ZOOM  && !$isMobile
        ?  "400px"
        :  $carouselType === CAROUSEL_TYPE.ZOOM_RELATED
          ? $isMobile ? "313px" : "350px"
          : "unset",
  overflow:  $carouselType === CAROUSEL_TYPE.ZOOM ? "visible" : ($isMobile ? "visible" : "hidden"),
  zIndex: "unset",

  paddingLeft: ($carouselType === CAROUSEL_TYPE.ZOOM
    ||  $carouselType === CAROUSEL_TYPE.ZOOM_RELATED
  ) ?  "20px" : undefined,
  paddingTop:$carouselType === CAROUSEL_TYPE.ZOOM_RELATED ? "80px" : undefined,

  transform: ($carouselType === CAROUSEL_TYPE.ZOOM ) ? "translateX(-20px)" :
    ($carouselType === CAROUSEL_TYPE.ZOOM_RELATED ) ? "translate(-20px, -80px)"
      : undefined,

  "&:has(:hover)":{
    zIndex:  ($carouselType === CAROUSEL_TYPE.ZOOM  ||  $carouselType === CAROUSEL_TYPE.ZOOM_RELATED) ?
      theme.zIndex.appBar : undefined

  },

  ".swiper-pagination": {
    bottom: $carouselType === CAROUSEL_TYPE.HERO  ? "1rem"  : "1.75rem",
    position:( $carouselType === CAROUSEL_TYPE.ZOOM_RELATED ||
     $carouselType === CAROUSEL_TYPE.ZOOM ||
     $carouselType === CAROUSEL_TYPE.HERO) && !$isMobile ? "absolute !important" : "static",
    width:$carouselType === CAROUSEL_TYPE.ZOOM_RELATED ? "50% !important" : undefined
  },

  ".swiper-pagination-bullet": {
    backgroundColor: theme.customColors.systemPrimary01,
    height: "0.75rem",
    width: "0.75rem",
    opacity: "0.6 !important",
    border: `1px solid ${theme.customColors.borderPrimaryCta}`
  },

  ".swiper-pagination-bullet-active": {
    borderRadius: "0.9375rem",
    boxShadow: `0px 0px 4px 0px ${theme.customColors.systemPrimary01}`,
    width: "2.5rem",
    opacity: "1 !important",
    border: `1px solid ${theme.customColors.borderPrimaryCta}`
  },

  ".swiper-slide": {
    alignItems:"center",
    maxWidth: $carouselType === CAROUSEL_TYPE.HERO  ? "100%"  : "fit-content",

    height:"auto",

    ":has(:hover)": {
      zIndex: 9999
    },

    "&.swiper-slide-active":{
      [`${StyledPortalWrapper}`]:{
        transform: $carouselType === CAROUSEL_TYPE.ZOOM ?  "translateX(0px)" : "translateX(35px)"
      }
    },
    "&.swiper-slide-next + div ":{
      "&:hover,&:focus-visible":{
        [`${StyledPortalWrapper}`]:{
          transform: $carouselType === CAROUSEL_TYPE.ZOOM ?  "translateX(-43px)" : "translateX(30px)"
        }
      }

    },

    "&.swiper-slide-next + div + div":{
      "&:hover,&:focus-visible":{
        [`${StyledPortalWrapper}`]:{
          transform:  $carouselType === CAROUSEL_TYPE.ZOOM_RELATED ?  "translateX(-47px)" : "translateX(35px)"
        }
      }
    }
  },

  ".swiper-wrapper": {
    alignItems: "stretch",
    maxWidth: "100vw",
    overflowY:"visible",
    position:"relative",


    ":has(:hover)": {
      zIndex: ($carouselType === CAROUSEL_TYPE.ZOOM || $carouselType === CAROUSEL_TYPE.ZOOM_RELATED)
        ?  theme.zIndex.appBar :
        1
    }
  },

  ".swiper-button-next, .swiper-button-prev":   {
    borderRadius: "50%",
    height: "60px",
    margin: "auto 0",
    opacity:0,
    right:"-30px",
    top: "50%",
    transition: "opacity .2s linear",
    width: "60px",

    "&:after":{
      backgroundRepeat:"no-repeat",
      content:"''",
      height:"48px",
      width:"48px"
    }
  },

  ".swiper-button-next":{
    background: "transparent",
    transform: "translate(-50%,-50%)",

    [theme.breakpoints.down("sm")]:{
      display: "none"
    },

    "&:after":{
      // eslint-disable-next-line max-len
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='48' height='48' viewBox='0 0 48 48' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='48' height='48' rx='24' fill='%23${theme.customColors.backgroundPrimaryCta.replace("#", "")}'/%3E%3Cpath d='M18.37 10.2424C19.1982 10.2424 19.9805 10.4714 20.5556 10.8831L34.3598 20.4726C35.2571 21.1134 35.8093 22.0746 35.9013 23.1503C35.9703 24.203 35.5792 25.1871 34.7739 25.9424C34.3138 26.3772 33.5776 26.3544 33.1404 25.8966C32.7033 25.4389 32.7263 24.7065 33.1865 24.2717C33.4855 23.9971 33.6236 23.6767 33.6006 23.3334C33.5776 22.9672 33.3705 22.601 33.0254 22.3722L19.2212 12.7599C18.8071 12.4854 17.6107 12.4168 17.1046 13.0116C16.8515 13.3092 16.7135 13.6983 16.7825 14.0873C16.8285 14.4764 17.0356 14.8426 17.3807 15.0715L25.111 20.4726C26.1003 21.1592 26.6985 22.2806 26.6985 23.4936C26.6985 24.1344 26.1923 24.6379 25.5482 24.6379C24.927 24.6379 24.3978 24.1344 24.3978 23.4936C24.3978 23.0359 24.1677 22.601 23.7996 22.3264L16.0693 16.9252C15.218 16.3302 14.6428 15.3918 14.5278 14.362C14.3898 13.3322 14.7119 12.2793 15.4021 11.5013C16.1613 10.6315 17.3116 10.2425 18.393 10.2425L18.37 10.2425L18.37 10.2424Z' fill='%23${theme.customColors.textPrimaryCta.replace("#", "")}'/%3E%3Cpath d='M25.5246 22.3492C26.1687 22.3492 26.6749 22.8756 26.6749 23.4935C26.6749 24.6836 26.0767 25.8279 25.0874 26.5145L17.3571 31.9156C17.035 32.1445 16.8279 32.4878 16.7589 32.8998C16.7129 33.2888 16.8279 33.6779 17.081 33.9754C17.5872 34.5476 18.8065 34.5018 19.1977 34.2272L29.2057 27.2698C29.7349 26.9036 30.4481 27.0409 30.8162 27.5444C31.1843 28.0708 31.0463 28.7803 30.5401 29.1465L20.5551 36.1268C19.2207 37.0422 16.6669 36.9736 15.3785 35.4859C14.6883 34.7078 14.3662 33.655 14.5043 32.6252C14.6423 31.5953 15.1945 30.6569 16.0688 30.0619L23.7761 24.6608C24.1672 24.3861 24.3743 23.9742 24.3743 23.4935C24.3743 22.8756 24.9034 22.3492 25.5246 22.3492L25.5246 22.3492Z' fill='%23${theme.customColors.textPrimaryCta.replace("#", "")}'/%3E%3C/svg%3E")`
    }
  },
  ".swiper-button-prev":{
    background: "transparent",
    left:"0" ,
    right:undefined,
    transform: "translate(50%,-50%)",

    [theme.breakpoints.down("sm")]:{
      display: "none"
    },

    "&:after":{
      transform: "scaleX(-1)",
      // eslint-disable-next-line max-len
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='48' height='48' viewBox='0 0 48 48' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='48' height='48' rx='24' fill='%23${theme.customColors.backgroundPrimaryCta.replace("#", "")}'/%3E%3Cpath d='M18.37 10.2424C19.1982 10.2424 19.9805 10.4714 20.5556 10.8831L34.3598 20.4726C35.2571 21.1134 35.8093 22.0746 35.9013 23.1503C35.9703 24.203 35.5792 25.1871 34.7739 25.9424C34.3138 26.3772 33.5776 26.3544 33.1404 25.8966C32.7033 25.4389 32.7263 24.7065 33.1865 24.2717C33.4855 23.9971 33.6236 23.6767 33.6006 23.3334C33.5776 22.9672 33.3705 22.601 33.0254 22.3722L19.2212 12.7599C18.8071 12.4854 17.6107 12.4168 17.1046 13.0116C16.8515 13.3092 16.7135 13.6983 16.7825 14.0873C16.8285 14.4764 17.0356 14.8426 17.3807 15.0715L25.111 20.4726C26.1003 21.1592 26.6985 22.2806 26.6985 23.4936C26.6985 24.1344 26.1923 24.6379 25.5482 24.6379C24.927 24.6379 24.3978 24.1344 24.3978 23.4936C24.3978 23.0359 24.1677 22.601 23.7996 22.3264L16.0693 16.9252C15.218 16.3302 14.6428 15.3918 14.5278 14.362C14.3898 13.3322 14.7119 12.2793 15.4021 11.5013C16.1613 10.6315 17.3116 10.2425 18.393 10.2425L18.37 10.2425L18.37 10.2424Z' fill='%23${theme.customColors.textPrimaryCta.replace("#", "")}'/%3E%3Cpath d='M25.5246 22.3492C26.1687 22.3492 26.6749 22.8756 26.6749 23.4935C26.6749 24.6836 26.0767 25.8279 25.0874 26.5145L17.3571 31.9156C17.035 32.1445 16.8279 32.4878 16.7589 32.8998C16.7129 33.2888 16.8279 33.6779 17.081 33.9754C17.5872 34.5476 18.8065 34.5018 19.1977 34.2272L29.2057 27.2698C29.7349 26.9036 30.4481 27.0409 30.8162 27.5444C31.1843 28.0708 31.0463 28.7803 30.5401 29.1465L20.5551 36.1268C19.2207 37.0422 16.6669 36.9736 15.3785 35.4859C14.6883 34.7078 14.3662 33.655 14.5043 32.6252C14.6423 31.5953 15.1945 30.6569 16.0688 30.0619L23.7761 24.6608C24.1672 24.3861 24.3743 23.9742 24.3743 23.4935C24.3743 22.8756 24.9034 22.3492 25.5246 22.3492L25.5246 22.3492Z' fill='%23${theme.customColors.textPrimaryCta.replace("#", "")}'/%3E%3C/svg%3E")`

    }
  },

  "&:hover":{
    ".swiper-button-next, .swiper-button-prev":{
      "&:not(.swiper-button-disabled)":{
        opacity: 1,
        transition: "opacity .2s linear"
      },
      zIndex: 9998
    }
  },

  [theme.breakpoints.down("max")]:{

    ".swiper-slide":{

      "&.swiper-slide-next + div + div":{
        "&:hover,&:focus-visible":{
          [`${StyledPortalWrapper}`]:{
            right:  $carouselType === CAROUSEL_TYPE.ZOOM_RELATED  ?  "40%" : undefined,
            transform:  $carouselType === CAROUSEL_TYPE.ZOOM_RELATED ?  "none" : undefined
          }
        }

      }
    }
  },

  "@media (max-width: 1394.95px)":{
    ".swiper-slide": {
      "&.swiper-slide-next + div ":{
        "&:hover,&:focus-visible":{
          [`${StyledPortalWrapper}`]:{
            right:  $carouselType === CAROUSEL_TYPE.ZOOM  ?  "86px" : undefined,
            transform:
              $carouselType === CAROUSEL_TYPE.ZOOM
                ?  "none" : "translateX(30px)"
          }
        }

      },
      "&.swiper-slide-next + div + div":{
        "&:hover,&:focus-visible":{
          [`${StyledPortalWrapper}`]:{
          }
        }

      }
    }
  },

  "@media (max-width: 1313.95px)":{
    ".swiper-slide": {
      "&.swiper-slide-next + div ":{
        "&:hover,&:focus-visible":{
          [`${StyledPortalWrapper}`]:{
            right:  $carouselType === CAROUSEL_TYPE.ZOOM ? "80%" : undefined,
            transform: $carouselType === CAROUSEL_TYPE.ZOOM ?  "none" : "translateX(30px)"
          }
        }

      },
      "&.swiper-slide-next + div + div":{
        "&:hover,&:focus-visible":{
          [`${StyledPortalWrapper}`]:{
            right:  $carouselType === CAROUSEL_TYPE.ZOOM_RELATED  ?  "calc(90% + 10%)" : undefined

          }
        }

      }
    }
  },

  "@media (max-width: 1269px)":{
    ".swiper-slide": {

      "&.swiper-slide-next + div + div":{
        "&:hover,&:focus-visible":{
          [`${StyledPortalWrapper}`]:{
            right:  $carouselType === CAROUSEL_TYPE.ZOOM_RELATED  ?  "calc(90%)" : undefined

          }
        }

      }
    }
  },

  [theme.breakpoints.down("lg")]:{
    ".swiper-slide": {
      "&.swiper-slide-next + div ":{
        "&:hover,&:focus-visible":{
          [`${StyledPortalWrapper}`]:{
            right:  $carouselType === CAROUSEL_TYPE.ZOOM ? "80%" : "30%"
          }
        }

      },
      "&.swiper-slide-next + div + div":{
        "&:hover,&:focus-visible":{
          [`${StyledPortalWrapper}`]:{
            right:  $carouselType === CAROUSEL_TYPE.ZOOM_RELATED  ? "100%": undefined
          }
        }

      }
    }
  },

  "@media (max-width: 1074px)":{
    ".swiper-slide": {
      "&.swiper-slide-next ":{
        "&:hover,&:focus-visible":{
          [`${StyledPortalWrapper}`]:{
            right: $carouselType === CAROUSEL_TYPE.ZOOM ? "88px" : undefined
          }
        }

      },
      "&.swiper-slide-next + div ":{
        "&:hover,&:focus-visible":{
          [`${StyledPortalWrapper}`]:{
            right:  $carouselType === CAROUSEL_TYPE.ZOOM ? "0px" :
              $carouselType === CAROUSEL_TYPE.ZOOM_RELATED  ?  "56%" : undefined,
            transform:$carouselType === CAROUSEL_TYPE.ZOOM ?  "none" :
              "translateX(30px)"
          }
        }

      }
    }
  },

  [theme.breakpoints.down("md")]:{
    ".swiper-slide": {
      "&.swiper-slide-next ":{
        "&:hover,&:focus-visible":{
          [`${StyledPortalWrapper}`]:{
            right: $carouselType === CAROUSEL_TYPE.ZOOM ? "88px" : "20px"
          }
        }

      }
    }
  },

  [theme.breakpoints.down("sm")]:{
    margin: 0,
    paddingTop:"0px",

    transform: ($carouselType === CAROUSEL_TYPE.ZOOM ) ? "translateX(-20px)" :
      ($carouselType === CAROUSEL_TYPE.ZOOM_RELATED ) ? "none"
        : undefined,

    ".swiper-slide": {
      maxWidth: ($carouselType === CAROUSEL_TYPE.HERO) || $label === "keep-learning" ? "100%" : "fit-content"
    },

    "&.swiper-horizontal>.swiper-pagination-bullets.swiper-pagination-bullets-dynamic":{
      transform:$carouselType === CAROUSEL_TYPE.ZOOM_RELATED ? "translateX(50%)" : undefined

    },
    ".swiper-pagination": {
      margin:"16px auto 0",
      position: "static",
      transform:$carouselType === CAROUSEL_TYPE.ZOOM || $carouselType === CAROUSEL_TYPE.ZOOM_RELATED ?
        "none !important" : undefined

    }
  }

}));
